import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import adminOpsService from '../../services/adminOpsService';
import {
  Lock,
  Plus,
  Search,
  RefreshCw,
  User,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Eye,
  Calendar,
  CheckCircle,
  XCircle,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const PERMISSION_LEVELS = [
  { value: 'full_admin', label: 'Full Admin', description: 'Complete access to all admin sections', icon: ShieldCheck, color: 'text-emerald-400' },
  { value: 'ai_ops', label: 'AI Operations', description: 'Access to AI training and configuration', icon: Shield, color: 'text-emerald-400' },
  { value: 'readonly', label: 'Read Only', description: 'View-only access to admin sections', icon: Eye, color: 'text-slate-400' },
];

const ADMIN_SECTIONS = [
  'admin_overview',
  'parser_training',
  'prompt_management',
  'knowledge_base',
  'letter_training',
  'workflow_rules',
  'method_library',
  'template_manager',
  'review_queue',
  'sandbox_tester',
  'system_status',
  'permissions',
];

const PermissionCard = ({ permission }) => {
  const levelConfig = PERMISSION_LEVELS.find(l => l.value === permission.permission_level) || PERMISSION_LEVELS[2];
  const LevelIcon = levelConfig.icon;
  const isExpired = permission.expires_at && new Date(permission.expires_at) < new Date();

  return (
    <div 
      className={`bg-stone-900 border rounded-xl p-5 ${isExpired ? 'border-red-500/30 opacity-60' : 'border-stone-800'}`}
      data-testid={`permission-${permission.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg bg-stone-800 flex items-center justify-center`}>
            <User className="text-slate-400" size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-white">{permission.user_id}</h3>
            <div className="flex items-center gap-1.5">
              <LevelIcon className={levelConfig.color} size={14} />
              <span className={`text-sm ${levelConfig.color}`}>{levelConfig.label}</span>
            </div>
          </div>
        </div>
        {permission.is_active && !isExpired ? (
          <span className="px-2 py-1 text-xs bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 rounded-full">
            Active
          </span>
        ) : (
          <span className="px-2 py-1 text-xs bg-red-500/10 text-red-400 border border-red-500/30 rounded-full">
            {isExpired ? 'Expired' : 'Inactive'}
          </span>
        )}
      </div>

      {/* Allowed Sections */}
      {permission.allowed_sections?.length > 0 && (
        <div className="mb-3">
          <p className="text-xs text-slate-500 mb-1">Allowed Sections</p>
          <div className="flex flex-wrap gap-1">
            {permission.allowed_sections.slice(0, 4).map(section => (
              <span key={section} className="px-2 py-0.5 text-xs bg-emerald-500/10 text-emerald-400 rounded">
                {section.replace('_', ' ')}
              </span>
            ))}
            {permission.allowed_sections.length > 4 && (
              <span className="px-2 py-0.5 text-xs bg-stone-800 text-slate-500 rounded">
                +{permission.allowed_sections.length - 4} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Denied Sections */}
      {permission.denied_sections?.length > 0 && (
        <div className="mb-3">
          <p className="text-xs text-slate-500 mb-1">Denied Sections</p>
          <div className="flex flex-wrap gap-1">
            {permission.denied_sections.map(section => (
              <span key={section} className="px-2 py-0.5 text-xs bg-red-500/10 text-red-400 rounded">
                {section.replace('_', ' ')}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Notes */}
      {permission.notes && (
        <p className="text-sm text-slate-400 mb-3 line-clamp-2">{permission.notes}</p>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-3 border-t border-stone-800 text-xs text-slate-500">
        <span>Granted: {new Date(permission.granted_at).toLocaleDateString()}</span>
        {permission.expires_at && (
          <span className="flex items-center gap-1">
            <Calendar size={12} />
            Expires: {new Date(permission.expires_at).toLocaleDateString()}
          </span>
        )}
      </div>
    </div>
  );
};

const GrantPermissionDialog = ({ onSave, onClose }) => {
  const [formData, setFormData] = useState({
    user_id: '',
    permission_level: 'ai_ops',
    allowed_sections: [],
    denied_sections: [],
    expires_at: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  const toggleSection = (section, type) => {
    const key = type === 'allow' ? 'allowed_sections' : 'denied_sections';
    const otherKey = type === 'allow' ? 'denied_sections' : 'allowed_sections';
    
    setFormData(prev => ({
      ...prev,
      [key]: prev[key].includes(section)
        ? prev[key].filter(s => s !== section)
        : [...prev[key], section],
      // Remove from other list if present
      [otherKey]: prev[otherKey].filter(s => s !== section),
    }));
  };

  const handleSubmit = async () => {
    if (!formData.user_id || !formData.permission_level) {
      toast.error('User ID and permission level are required');
      return;
    }
    setSaving(true);
    try {
      await onSave({
        ...formData,
        expires_at: formData.expires_at || null,
      });
      toast.success('Permission granted successfully');
      onClose();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent className="max-w-2xl bg-stone-900 border-stone-800 text-white">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Shield className="text-amber-400" size={20} />
          Grant Admin Permission
        </DialogTitle>
        <DialogDescription className="text-slate-400">
          Grant admin access to a user. This action is logged and audited.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 py-4">
        {/* User & Level */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-slate-400 mb-1 block">User ID *</label>
            <Input
              value={formData.user_id}
              onChange={(e) => setFormData(prev => ({ ...prev, user_id: e.target.value }))}
              placeholder="user_xxxxx"
              className="bg-stone-800 border-slate-700 text-white"
            />
          </div>
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Permission Level *</label>
            <Select
              value={formData.permission_level}
              onValueChange={(value) => setFormData(prev => ({ ...prev, permission_level: value }))}
            >
              <SelectTrigger className="bg-stone-800 border-slate-700 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-stone-800 border-slate-700">
                {PERMISSION_LEVELS.map(level => (
                  <SelectItem key={level.value} value={level.value}>
                    <div className="flex items-center gap-2">
                      <level.icon size={14} className={level.color} />
                      {level.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Expiration */}
        <div>
          <label className="text-sm text-slate-400 mb-1 block">Expires At (Optional)</label>
          <Input
            type="datetime-local"
            value={formData.expires_at}
            onChange={(e) => setFormData(prev => ({ ...prev, expires_at: e.target.value }))}
            className="bg-stone-800 border-slate-700 text-white"
          />
        </div>

        {/* Section Access */}
        <div>
          <label className="text-sm text-slate-400 mb-2 block">Section Access</label>
          <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto">
            {ADMIN_SECTIONS.map(section => {
              const isAllowed = formData.allowed_sections.includes(section);
              const isDenied = formData.denied_sections.includes(section);
              return (
                <div key={section} className="flex items-center gap-2 bg-stone-800 rounded-lg p-2">
                  <button
                    onClick={() => toggleSection(section, 'allow')}
                    className={`p-1 rounded ${isAllowed ? 'bg-emerald-500 text-white' : 'bg-slate-700 text-slate-400'}`}
                  >
                    <CheckCircle size={12} />
                  </button>
                  <button
                    onClick={() => toggleSection(section, 'deny')}
                    className={`p-1 rounded ${isDenied ? 'bg-red-500 text-white' : 'bg-slate-700 text-slate-400'}`}
                  >
                    <XCircle size={12} />
                  </button>
                  <span className="text-xs text-slate-300 capitalize">{section.replace('_', ' ')}</span>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Green = Allow, Red = Deny, Neither = Default (based on permission level)
          </p>
        </div>

        {/* Notes */}
        <div>
          <label className="text-sm text-slate-400 mb-1 block">Notes</label>
          <Textarea
            value={formData.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Reason for granting access..."
            className="bg-stone-800 border-slate-700 text-white resize-none"
            rows={2}
          />
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={saving}>
          {saving ? <RefreshCw size={16} className="mr-2 animate-spin" /> : <Shield size={16} className="mr-2" />}
          Grant Permission
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};

const PermissionsPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isGranting, setIsGranting] = useState(false);

  const canManagePermissions = ['platform_owner', 'platform_admin'].includes(user?.role);

  const fetchPermissions = useCallback(async () => {
    if (!canManagePermissions) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      const data = await adminOpsService.listPermissions();
      setPermissions(data.permissions || []);
    } catch (error) {
      if (error.message.includes('403')) {
        // Expected for non-platform owners
      } else {
        toast.error('Failed to load permissions');
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, canManagePermissions]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const handleSave = async (formData) => {
    adminOpsService.setAuthHeaders(getAuthHeaders());
    await adminOpsService.grantPermission(formData);
    fetchPermissions();
  };

  const filteredPermissions = permissions.filter(perm =>
    perm.user_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    perm.notes?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!canManagePermissions) {
    return (
      <div className="space-y-6" data-testid="permissions-page">
        <div className="flex items-center gap-3">
          <Lock className="text-amber-400" size={28} />
          <h1 className="text-2xl font-bold text-white">Access / Permissions</h1>
        </div>
        
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6 text-center">
          <ShieldAlert className="mx-auto text-amber-400 mb-4" size={48} />
          <h2 className="text-xl font-semibold text-white mb-2">Restricted Access</h2>
          <p className="text-slate-400">
            Only Platform Owners and Platform Admins can view and manage admin permissions.
          </p>
          <p className="text-sm text-slate-500 mt-4">
            Your current role: <span className="text-white capitalize">{user?.role?.replace('_', ' ')}</span>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="permissions-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Lock className="text-amber-400" size={28} />
            Access / Permissions
          </h1>
          <p className="text-slate-500 mt-1">
            Manage admin access permissions for users
          </p>
        </div>
        <Button onClick={() => setIsGranting(true)}>
          <Plus size={18} className="mr-2" />
          Grant Permission
        </Button>
      </div>

      {/* Warning Banner */}
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="text-amber-400 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h3 className="font-semibold text-amber-400">Security Notice</h3>
            <p className="text-sm text-slate-400">
              All permission changes are logged and audited. Only grant access to trusted users.
              Permissions with expiration dates will automatically become inactive.
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by user ID..."
            className="pl-10 bg-stone-900 border-stone-800 text-white placeholder:text-slate-500"
          />
        </div>
        <Button variant="outline" size="icon" onClick={fetchPermissions}>
          <RefreshCw size={18} />
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-stone-900 border border-stone-800 rounded-lg p-4">
          <p className="text-slate-500 text-sm">Total Permissions</p>
          <p className="text-2xl font-bold text-white">{permissions.length}</p>
        </div>
        <div className="bg-stone-900 border border-stone-800 rounded-lg p-4">
          <p className="text-slate-500 text-sm">Active</p>
          <p className="text-2xl font-bold text-emerald-400">
            {permissions.filter(p => p.is_active && (!p.expires_at || new Date(p.expires_at) > new Date())).length}
          </p>
        </div>
        <div className="bg-stone-900 border border-stone-800 rounded-lg p-4">
          <p className="text-slate-500 text-sm">Full Admin</p>
          <p className="text-2xl font-bold text-amber-400">
            {permissions.filter(p => p.permission_level === 'full_admin').length}
          </p>
        </div>
        <div className="bg-stone-900 border border-stone-800 rounded-lg p-4">
          <p className="text-slate-500 text-sm">Expired</p>
          <p className="text-2xl font-bold text-slate-400">
            {permissions.filter(p => p.expires_at && new Date(p.expires_at) < new Date()).length}
          </p>
        </div>
      </div>

      {/* Permissions Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-48 bg-stone-800" />)}
        </div>
      ) : filteredPermissions.length === 0 ? (
        <div className="text-center py-16 bg-stone-900 rounded-xl border border-stone-800">
          <Lock className="mx-auto text-slate-600 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-white mb-2">No Permissions Found</h3>
          <p className="text-slate-500 mb-4">
            {searchQuery ? 'No permissions match your search.' : 'No custom permissions have been granted yet.'}
          </p>
          <Button onClick={() => setIsGranting(true)}>
            <Plus size={18} className="mr-2" />
            Grant Permission
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPermissions.map(permission => (
            <PermissionCard key={permission.id} permission={permission} />
          ))}
        </div>
      )}

      {/* Grant Dialog */}
      <Dialog open={isGranting} onOpenChange={setIsGranting}>
        <GrantPermissionDialog
          onSave={handleSave}
          onClose={() => setIsGranting(false)}
        />
      </Dialog>
    </div>
  );
};

export default PermissionsPage;
